import React, { useEffect, useState, useRef } from 'react';
import { PlayIcon, PauseIcon, HeadphonesIcon, Volume2Icon } from 'lucide-react';
const AUDIO_SRC = 'https://cdn.magicpatterns.com/uploads/fhDVoxBkeQvsjBNPXQLe1b/Audio_prewiu_Every_time_you_watch.mp3';
const BAR_COUNT = 5;
const IDLE_BARS = Array(BAR_COUNT).fill(0.25);
function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
export function AudioPreview() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bars, setBars] = useState<number[]>(IDLE_BARS);
  // Setup analyser on first play (must happen after user gesture)
  const ensureAudioContext = () => {
    if (audioCtxRef.current || !audioRef.current) return;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const source = ctx.createMediaElementSource(audioRef.current);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.75;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
    } catch {
      // CORS or unsupported — bars will gracefully animate via fallback
    }
  };
  const tick = () => {
    const analyser = analyserRef.current;
    if (analyser) {
      const buf = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(buf);
      const out: number[] = [];
      const step = Math.max(1, Math.floor(buf.length / BAR_COUNT));
      for (let i = 0; i < BAR_COUNT; i++) {
        let sum = 0;
        let count = 0;
        for (let j = i * step; j < (i + 1) * step && j < buf.length; j++) {
          sum += buf[j];
          count++;
        }
        const avg = count ? sum / count : 0;
        out.push(Math.max(0.15, avg / 255));
      }
      setBars(out);
    } else {
      // Fallback: animate bars with sine waves so it still feels alive
      const t = performance.now() / 1000;
      const out: number[] = [];
      for (let i = 0; i < BAR_COUNT; i++) {
        out.push(0.35 + 0.35 * Math.abs(Math.sin(t * (3 + i * 0.6) + i)) + 0.1 * Math.random());
      }
      setBars(out);
    }
    rafRef.current = requestAnimationFrame(tick);
  };
  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      return;
    }
    ensureAudioContext();
    try {
      if (audioCtxRef.current?.state === 'suspended') {
        await audioCtxRef.current.resume();
      }
      await audio.play();
    } catch {
      // Play promise rejected; bail silently
    }
  };
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => {
      setIsPlaying(true);
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    const onPause = () => {
      setIsPlaying(false);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setBars(IDLE_BARS);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setBars(IDLE_BARS);
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMeta = () => setDuration(audio.duration);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMeta);
    audio.addEventListener('durationchange', onLoadedMeta);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
      audio.removeEventListener('durationchange', onLoadedMeta);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  return <div className="w-full max-w-md mx-auto" data-id="element-20">
      <div className="text-center text-gold tracking-[0.25em] uppercase text-[10px] sm:text-xs mb-3" data-id="element-21">
        Audio Preview
      </div>

      <div className="relative bg-charcoal-light/80 border border-gold/25 rounded-2xl p-4 sm:p-5 backdrop-blur-sm" data-id="element-22">
        {/* Top row: play + title + bars */}
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-23">
          {/* Play button */}
          <button onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'} className="relative shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-gold to-gold-dark text-charcoal flex items-center justify-center shadow-glow-gold transition-transform hover:scale-105 active:scale-95" data-id="element-24">
            {/* Soft pulsing ring while playing */}
            {isPlaying && <span className="absolute inset-0 rounded-full border border-gold/60 animate-ping" data-id="element-25" />}
            {isPlaying ? <PauseIcon className="w-5 h-5 sm:w-6 sm:h-6 fill-current" data-id="element-26" /> : <PlayIcon className="w-5 h-5 sm:w-6 sm:h-6 fill-current translate-x-0.5" data-id="element-27" />}
          </button>

          {/* Title + subtitle */}
          <div className="flex-1 min-w-0" data-id="element-28">
            <div className="flex items-center gap-2 text-ivory font-medium text-sm sm:text-base" data-id="element-29">
              <HeadphonesIcon className="w-4 h-4 text-gold/80 shrink-0" data-id="element-30" />
              <span className="truncate" data-id="element-31">Audio Preview</span>
            </div>
            <div className="text-ivory/55 text-xs sm:text-sm mt-0.5" data-id="element-32">
              Listen before you buy
            </div>
          </div>

          {/* Live bars visualizer */}
          <div className="flex items-end gap-[3px] h-7 sm:h-8 shrink-0" aria-hidden="true" data-id="element-33">
            {bars.map((b, i) => <span key={i} className="block w-[3px] sm:w-[4px] rounded-full bg-gold" style={{
            height: `${Math.max(15, Math.min(100, b * 100))}%`,
            transition: 'height 80ms linear',
            opacity: isPlaying ? 1 : 0.45
          }} data-id="element-34" />)}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 sm:mt-5" data-id="element-35">
          <div className="relative h-[3px] rounded-full bg-ivory/10 overflow-hidden" data-id="element-36">
            <div className="absolute inset-y-0 left-0 bg-gold rounded-full transition-[width] duration-150 ease-linear" style={{
            width: `${progress}%`
          }} data-id="element-37" />
          </div>

          {/* Time row */}
          <div className="mt-2 flex items-center justify-between text-[11px] sm:text-xs" data-id="element-38">
            <span className="text-ivory/60 tabular-nums" data-id="element-39">
              {formatTime(currentTime)}
            </span>
            <span className="text-ivory/50 flex items-center gap-1.5" data-id="element-40">
              <Volume2Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-41" />
              <span className="tracking-wider uppercase text-[10px] sm:text-[11px]" data-id="element-42">
                Preview
              </span>
            </span>
            <span className="text-ivory/60 tabular-nums" data-id="element-43">
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Hidden audio element — crossOrigin for analyser access */}
        <audio ref={audioRef} src={AUDIO_SRC} preload="metadata" crossOrigin="anonymous" playsInline data-id="element-44" />
      </div>
    </div>;
}